import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Select } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { SELECT_INPUT_FOCUS_DELAY } from "../../constants";
import { useGlobalNewOpportunityModalContext } from "../../context";
import { useGlobalCreateOpportunityStore } from "../../store";
import DDClearIcon from "../DDClearIcon";

import { DefaultDDRenderer } from "./DefaultDDRenderer";

interface Props {
  arrtibuteName: string;
}

export const SelectWithFormItem = ({ arrtibuteName }: Props) => {
  const [openSelect, setOpenSelect] = useState(false);
  const ref = useRef<any>(null);
  const { form } = useGlobalNewOpportunityModalContext();
  const {
    selectTypeAttributes,
    setSelectActiveAttributes,
    resetSelectTypeAttributes,
  } = useGlobalCreateOpportunityStore();

  // Helper function to ensure value is always an array
  const ensureArray = (value: any) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  };

  useEffect(() => {
    if (form) {
      const currentValue = form.getFieldValue(arrtibuteName);
      const normalizedValue = ensureArray(currentValue);

      if (currentValue !== normalizedValue) {
        form.setFieldValue(arrtibuteName, normalizedValue);
      }
    }
  }, [form, arrtibuteName]);

  const attribute = useMemo(() => {
    return selectTypeAttributes.find(
      attr => attr.property_name === arrtibuteName,
    );
  }, [selectTypeAttributes, arrtibuteName]);

  const handleDropdownClose = () => {
    setOpenSelect(false);
    // Focus the select after closing
    setTimeout(() => {
      ref.current?.focus();
    }, SELECT_INPUT_FOCUS_DELAY);
  };

  return (
    <Form.Item
      name={attribute?.property_name}
      label={attribute?.original_name}
      normalize={ensureArray}
      getValueFromEvent={value => ensureArray(value)}
    >
      <Select
        allowClear={{
          clearIcon: <DDClearIcon />,
        }}
        onClear={() => {
          if (attribute?.property_name) {
            form?.resetFields([attribute?.property_name]);
            resetSelectTypeAttributes(attribute?.property_name);
          }
        }}
        placeholder={`Select ${attribute?.current_name}`}
        ref={ref}
        onDropdownVisibleChange={e => {
          setOpenSelect(e);
        }}
        onFocus={() => {
          setOpenSelect(true);
        }}
        open={openSelect}
        suffixIcon={
          !!attribute?.options?.filter(option => option.active)?.length ? (
            <DDClearIcon asSuffix />
          ) : (
            <FontAwesomeIcon icon={faChevronDown} />
          )
        }
        value={ensureArray(form?.getFieldValue(attribute?.property_name))}
        labelRender={e => {
          const selectedValue = attribute?.options.find(
            option =>
              option.id === form?.getFieldValue(attribute?.property_name)?.[0],
          );
          return (
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">{selectedValue?.label}</p>
            </div>
          );
        }}
        dropdownRender={menu => {
          return (
            <DefaultDDRenderer
              items={attribute?.options ?? []}
              onItemSelect={e => {
                if (attribute) {
                  form?.setFieldsValue({
                    [attribute.property_name]: ensureArray(e.id),
                  });
                  setOpenSelect(false);
                  setSelectActiveAttributes({
                    property_name: attribute.property_name,
                    optionId: String(e.id),
                  });
                  ref.current?.focus();
                }
              }}
              onCommandKeydown={e => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDropdownClose();
                }
              }}
            />
          );
        }}
      />
    </Form.Item>
  );
};
